import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import FieldHelp from '../ui/FieldHelp';
import { ENROLLMENT_FIELD_HELP as HELP } from '../student/enrollmentFieldHelp';
import axios from 'axios';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

export default function IndividualAssignmentDialog({ open, onClose, testId }) {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [students, setStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    if (open && userId) {
      fetchStudents();
      fetchAlreadyAssignedStudents();
    }
    // eslint-disable-next-line
  }, [open, userId]);

  const fetchStudents = async () => {
  setLoading(true);
  try {
    const response = await axios.get(
      `${apiUrl}/api/individual-Student/${userId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: getBasicAuthHeader(),
        },
      }
    );

    if (response.data && Array.isArray(response.data)) {
      // Remove duplicates by studentId
      const uniqueStudents = response.data.filter(
        (student, index, self) =>
          index === self.findIndex((s) => s.studentId === student.studentId)
      );

      setStudents(uniqueStudents);
    } else {
      setStudents([]);
    }
  } catch (error) {
    console.error("Error fetching students:", error);
    setStudents([]);
  } finally {
    setLoading(false);
  }
};

const fetchAlreadyAssignedStudents = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/api/individual-Student-By-Test/${userId}/${testId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: getBasicAuthHeader(),
        },
      }
    );

    if (response.data && Array.isArray(response.data)) {
      // Extract only studentIds from the response
      const preSelectedIds = response.data.map((s) => s.studentId);
      setSelectedStudentIds(preSelectedIds);
    }
  } catch (error) {
    console.error("Error fetching already assigned students:", error);
  }
};

const handleAssign = async () => {
  if (!guardAction('action.assessment.settings.individualAssign')) return;
  if (!selectedStudentIds.length) {
    alert("Please select at least one student");
    return;
  }

  setIsLoading(true)

  try {
    const payload = {
      userId: userId, // from your state
      testId: testId, // from your state
      studentIds: selectedStudentIds, // array of selected studentId
    };

    await axios.post(`${apiUrl}/api/student-assessment/assign`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: getBasicAuthHeader(),
      },
    });

    alert("Students assigned successfully");
    setIsLoading(false)
    onClose();
    
  } catch (error) {
    console.error("Error assigning students:", error);
    setIsLoading(false)
    alert("Failed to assign students");
    
  }
};

  const handleChange = (event) => {
    setSelectedStudentIds(event.target.value);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Individual Assignment
        </DialogTitle>
  
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select one or more students from the list below to assign this assessment individually.
          </DialogContentText>
  
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <FormControl fullWidth>
              <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                Select Learners
                <FieldHelp text={HELP.selectLearners} />
              </InputLabel>
              <Select
                multiple
                value={selectedStudentIds}
                onChange={handleChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const student = students.find((s) => s.studentId === id);
                      return <Chip key={id} label={student ? student.fullName : id} />;
                    })}
                  </Box>
                )}
              >
                {students.map((student) => (
                  <MenuItem key={student.studentId} value={student.studentId}>
                    {student.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
  
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="error">
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            color="primary"
            disabled={selectedStudentIds.length === 0}
          >
            {isLoading ? <ButtonSpinnerSlot size={24} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
